import React, { useState } from 'react';
import axios from 'axios';
import { 
  X, 
  Plus, 
  Trash2, 
  ExternalLink, 
  Loader2,
  CheckCircle,
  AlertCircle,
  Link2,
  Globe,
  Info,
  Play,
  Settings,
  FileText,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

const CATEGORIES = [
  'External Content',
  'AICC Content',
  'Compliance & Regulatory',
  'Financial Services',
  'Risk Management',
  'Leadership & Management',
  'Professional Development'
];

export default function AICCCourseCreator({ token, onClose, onSuccess }) {
  const [courses, setCourses] = useState([{
    title: '',
    aicc_url: '',
    description: '',
    category: 'AICC Content',
    cpd_points: 0,
    estimated_duration: '',
    tutorial_id: '',
    tracking_mode: 'iframe_only' // 'iframe_only' or 'hacp' (future)
  }]);
  const [autoPublish, setAutoPublish] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  const parseAICCUrl = (url) => {
    // Extract tutorial ID from AICC URL if present
    try {
      const urlObj = new URL(url);
      const tutorialId = urlObj.searchParams.get('tutorialid') || '';
      return { baseUrl: url, tutorialId };
    } catch {
      return { baseUrl: url, tutorialId: '' };
    }
  };

  const addCourse = () => {
    setCourses([...courses, {
      title: '',
      aicc_url: '',
      description: '',
      category: 'AICC Content',
      cpd_points: 0,
      estimated_duration: '',
      tutorial_id: '',
      tracking_mode: 'iframe_only'
    }]);
  };

  const removeCourse = (index) => {
    if (courses.length > 1) {
      setCourses(courses.filter((_, i) => i !== index));
    }
  };

  const updateCourse = (index, field, value) => {
    const updated = [...courses];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-parse tutorial ID from URL
    if (field === 'aicc_url' && value) {
      const { tutorialId } = parseAICCUrl(value);
      if (tutorialId) {
        updated[index].tutorial_id = tutorialId;
      }
    }
    
    setCourses(updated);
  };

  const handleSubmit = async () => {
    const validCourses = courses.filter(c => c.title.trim() && c.aicc_url.trim());
    
    if (validCourses.length === 0) {
      toast.error('Please add at least one course with title and AICC URL');
      return;
    }

    setLoading(true);
    const creationResults = [];

    for (const course of validCourses) {
      try {
        // Build the full AICC URL with any additional parameters
        let fullUrl = course.aicc_url;
        
        // Create the course as an iframe/external course type
        const response = await axios.post(`${API}/admin/courses/aicc`, {
          title: course.title,
          description: course.description || `AICC course: ${course.title}`,
          category: course.category,
          cpd_points: parseInt(course.cpd_points) || 0,
          estimated_duration: course.estimated_duration || '1 hour',
          aicc_url: fullUrl,
          tutorial_id: course.tutorial_id,
          tracking_mode: course.tracking_mode,
          is_published: autoPublish,
          content_source: 'aicc',
          provider: 'Intuition' // Based on URL pattern
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        creationResults.push({
          title: course.title,
          success: true,
          course_id: response.data.course_id
        });
      } catch (error) {
        console.error('Error creating AICC course:', error);
        creationResults.push({
          title: course.title,
          success: false,
          error: error.response?.data?.detail || 'Failed to create course'
        });
      }
    }

    setResults(creationResults);
    setLoading(false);

    const successCount = creationResults.filter(r => r.success).length;
    if (successCount > 0) {
      toast.success(`Created ${successCount} AICC course(s) successfully`);
      onSuccess?.();
    }
  };

  const handlePreview = (url) => {
    setPreviewUrl(url);
    setShowPreview(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (results) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Creation Results</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-3">
          {results.map((result, index) => (
            <div 
              key={index}
              className={`p-4 rounded-lg border ${
                result.success 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-red-500/10 border-red-500/30'
              }`}
            >
              <div className="flex items-start gap-3">
                {result.success ? (
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{result.title}</p>
                  {result.success ? (
                    <p className="text-xs text-green-400 mt-1">
                      Course ID: {result.course_id}
                    </p>
                  ) : (
                    <p className="text-xs text-red-400 mt-1">{result.error}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => {
            setResults(null);
            setCourses([{
              title: '',
              aicc_url: '',
              description: '',
              category: 'AICC Content',
              cpd_points: 0,
              estimated_duration: '',
              tutorial_id: '',
              tracking_mode: 'iframe_only'
            }]);
          }}>
            Add More Courses
          </Button>
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-400" />
            AICC Course Embedder
          </h3>
          <p className="text-sm text-zinc-400 mt-1">
            Embed external AICC-compliant courses from platforms like Intuition
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-blue-300 font-medium">AICC Content Integration</p>
            <p className="text-zinc-400 mt-1">
              Paste the full AICC URL from your content provider. The course will be embedded 
              in an iframe. For full completion tracking, contact support for HACP integration.
            </p>
            <p className="text-zinc-500 text-xs mt-2">
              Example URL format: https://content.intuition.com/aicc/api/aicc/...?tutorialid=...
            </p>
          </div>
        </div>
      </div>

      {/* Courses List */}
      <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
        {courses.map((course, index) => (
          <div 
            key={index} 
            className="p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg space-y-4"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-zinc-300">
                Course {index + 1}
              </span>
              {courses.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeCourse(index)}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Course Title *</Label>
                <Input
                  value={course.title}
                  onChange={(e) => updateCourse(index, 'title', e.target.value)}
                  placeholder="e.g., Anti-Money Laundering Fundamentals"
                  className="mt-1 bg-zinc-900 border-zinc-600"
                />
              </div>

              <div className="col-span-2">
                <Label className="flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-blue-400" />
                  AICC URL *
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={course.aicc_url}
                    onChange={(e) => updateCourse(index, 'aicc_url', e.target.value)}
                    placeholder="https://content.intuition.com/aicc/api/aicc/..."
                    className="flex-1 bg-zinc-900 border-zinc-600 font-mono text-xs"
                  />
                  {course.aicc_url && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(course.aicc_url)}
                        className="border-zinc-600"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreview(course.aicc_url)}
                        className="border-zinc-600"
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {course.tutorial_id && (
                <div className="col-span-2">
                  <Label className="text-xs text-zinc-500">Detected Tutorial ID</Label>
                  <p className="text-xs text-zinc-400 font-mono bg-zinc-900 px-2 py-1 rounded mt-1">
                    {course.tutorial_id}
                  </p>
                </div>
              )}

              <div>
                <Label>Category</Label>
                <Select
                  value={course.category}
                  onValueChange={(v) => updateCourse(index, 'category', v)}
                >
                  <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>CPD Points</Label>
                <Input
                  type="number"
                  min="0"
                  value={course.cpd_points}
                  onChange={(e) => updateCourse(index, 'cpd_points', e.target.value)}
                  className="mt-1 bg-zinc-900 border-zinc-600"
                />
              </div>

              <div>
                <Label>Estimated Duration</Label>
                <Input
                  value={course.estimated_duration}
                  onChange={(e) => updateCourse(index, 'estimated_duration', e.target.value)}
                  placeholder="e.g., 45 minutes"
                  className="mt-1 bg-zinc-900 border-zinc-600"
                />
              </div>

              <div>
                <Label>Tracking Mode</Label>
                <Select
                  value={course.tracking_mode}
                  onValueChange={(v) => updateCourse(index, 'tracking_mode', v)}
                >
                  <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="iframe_only">Iframe Only (Basic)</SelectItem>
                    <SelectItem value="hacp" disabled>HACP Protocol (Coming Soon)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label>Description (Optional)</Label>
                <Textarea
                  value={course.description}
                  onChange={(e) => updateCourse(index, 'description', e.target.value)}
                  placeholder="Brief description of the course content..."
                  rows={2}
                  className="mt-1 bg-zinc-900 border-zinc-600"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button
        variant="outline"
        onClick={addCourse}
        className="w-full border-dashed border-zinc-600 text-zinc-400 hover:text-white"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Another AICC Course
      </Button>

      {/* Options */}
      <div className="flex items-center gap-4 p-3 bg-zinc-800/50 rounded-lg">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={autoPublish}
            onChange={(e) => setAutoPublish(e.target.checked)}
            className="rounded border-zinc-600 bg-zinc-900"
          />
          <span className="text-sm text-zinc-300">Publish immediately</span>
        </label>
      </div>

      {/* Submit */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Creating...
            </>
          ) : (
            <>
              <Globe className="h-4 w-4 mr-2" />
              Create AICC Course{courses.length > 1 ? 's' : ''}
            </>
          )}
        </Button>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl h-[80vh] bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white">Course Preview</DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full min-h-[60vh]">
            {previewUrl && (
              <iframe
                src={previewUrl}
                className="w-full h-full rounded-lg border border-zinc-700"
                title="AICC Course Preview"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
